import React, { useState, useEffect, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import jwt from "jsonwebtoken";
import AppContext from "../app/AppContext";
import ApiService from "../services/ApiService";
import { firstLoggedInKey } from "./onboarding/OnboardingSplash";
import LoginForm from "./LoginForm";
import { Cookies } from "react-cookie";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../authConfigAzureAd";
import logo from "../resources/logo.png";
import tokenExpiredPlaceholder from "../resources/Images/Token-expired-placeholder-96px.svg";
import "bootstrap/dist/css/bootstrap.min.css";

const cookies = new Cookies();
const msalInstance = new PublicClientApplication(msalConfig);

const Login = ({ onLoginChange, setPage }) => {
  const context = useContext(AppContext);
  const {
    appSettingsConfig,
    loginSSO,
    authType,
    tokenReady,
    outlookComposeMode,
    currEmail,
    applicationEndpoint,
    freemiumUser
  } = context;
  const { apiUrl } = appSettingsConfig;
  const [valMsgs, setValMsgs] = useState("");
  const [passwordType, setPasswordType] = useState("password");
  const [confPasswordType, setConfPasswordType] = useState("password");
  const [gettingCookieCompleted, setGettingCookieCompleted] = useState(false);
  const [startTokenTimer, setStartTokenTimer] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [executingSSOLogin, setExecutingSSOLogin] = useState(false);

  const isFirstLogin = () => !localStorage.getItem(firstLoggedInKey);

  const showHidePassword = (e, passType) => {
    e.preventDefault();
    if (passType === "password") {
      setPasswordType(prev => (prev === "password" ? "text" : "password"));
    } else {
      setConfPasswordType(prev =>
        prev === "password" ? "text" : "password"
      );
    }
  };

  const openWebPage = () => {
    Office.context.ui.displayDialogAsync(applicationEndpoint, {
      height: 85,
      width: 60
    });
  };

  useEffect(() => {
    (async () => {
      await msalInstance.initialize();
    })();
  }, []);

  useEffect(() => {
    if (!loginSSO || executingSSOLogin) return;

    const token = cookies.get("triyo-token-app");
    const tokenApi = cookies.get("triyo-token-app-api");

    if (token) {
      setGettingCookieCompleted(true);
      setNewToken(token);
    } else if (tokenApi) {
      setGettingCookieCompleted(true);
      setNewToken(tokenApi);
    } else {
      setStartTokenTimer(true);
    }
  }, [loginSSO, executingSSOLogin]);

  useEffect(() => {
    if (
      !loginSSO ||
      executingSSOLogin ||
      gettingCookieCompleted ||
      !startTokenTimer
    )
      return;

    const intervalId = setInterval(() => {
      const token = cookies.get("triyo-token-app");
      const tokenApi = cookies.get("triyo-token-app-api");

      if (token || tokenApi) {
        setGettingCookieCompleted(true);
        setNewToken(token || tokenApi);
        clearInterval(intervalId);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [
    loginSSO,
    executingSSOLogin,
    gettingCookieCompleted,
    startTokenTimer
  ]);

  useEffect(() => {
    if (
      loginSSO &&
      gettingCookieCompleted &&
      !tokenReady &&
      !executingSSOLogin
    ) {
      ssoLogin();
    }
  }, [loginSSO, gettingCookieCompleted, tokenReady, executingSSOLogin]);

  const ssoLogin = useCallback(async () => {
    setExecutingSSOLogin(true);
    try {
      const response = await fetch(`${apiUrl}/users/GetCurrentUser`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${newToken}`
        }
      });
      const result = await response.json();

      localStorage.setItem("tmpTriyoToken", result.Token);
      localStorage.setItem("tmpJwtTriyoToken", result.AccessToken);

      context.userToken = result.Token;
      context.jwtUserToken = result.AccessToken;
      context.currUser = result.User;
      context.userName = result.User.UserName;
      context.userNameFull = result.User.Name;
      context.userEmail = result.User.Email;
      context.userLogin = true;
      context.tokenReady = true;

      onLoginChange(true);
    } finally {
      setExecutingSSOLogin(false);
    }
  }, [newToken, context, onLoginChange]);

  const loginAzureAd = useCallback(async () => {
    try {
      const authResponse = await msalInstance.loginPopup({
        scopes: ["user.read"]
      });

      const accessToken = authResponse.accessToken || "";
      const userJWT = jwt.decode(accessToken) || {};
      const username =
        authResponse.account?.username ||
        userJWT.preferred_username ||
        userJWT.upn ||
        userJWT.unique_name || "";
      const tenantId = authResponse.tenantId || userJWT.tid || "";
      const graphConnectorId = authResponse.idTokenClaims?.aud || userJWT.aud || userJWT.appid || "";
      const currDateOffset = new Date().getTimezoneOffset();

      const payload = {
        username,
        tenantId,
        graphConnectorId,
        graphToken: accessToken,
        timezone: currDateOffset
      };

      const result = await ApiService.azureUserLogin(payload, context);

      if (result.status !== 200 || !result.data.Token) {
        throw new Error("AzureUserLogin: token not received");
      }
      if (result.data.User.Active === false) {
        setValMsgs("This User Account is no longer Active. Contact Admin.");
        return;
      }

      localStorage.setItem("tmpTriyoToken", result.data.Token);
      localStorage.setItem("tmpJwtTriyoToken", result.data.AccessToken);

      context.userToken = result.data.Token;
      context.jwtUserToken = result.data.AccessToken;
      context.graphUserToken = result.data.GraphToken;
      context.currUser = result.data.User;
      context.userName = result.data.User.UserName;
      context.userNameFull = result.data.User.Name;
      context.userEmail = result.data.User.Email;
      context.userLogin = true;
      context.tokenReady = true;

      onLoginChange(true);
    } catch (err) {
      console.error(err);
      setValMsgs("Invalid username or password");
    }
  }, [context, onLoginChange]);

  const onSubmit = useCallback(
    values => {
      const data = {
        username: values.username,
        password: values.password,
        timezone: new Date().getTimezoneOffset()
      };

      ApiService.login(data, context)
        .then(result => {
          if (result.status !== 200) {
            console.log("API [login] non‑200 status:", result.status);
            return;
          }
          if (result.data.User.Active === false) {
            setValMsgs("This User Account is no longer Active. Contact Admin.");
            return;
          }

          localStorage.setItem("tmpTriyoToken", result.data.Token);
          localStorage.setItem("tmpJwtTriyoToken", result.data.AccessToken);
          localStorage.setItem("rememberUser", values.rememberChecked);

          context.userToken = result.data.Token;
          context.jwtUserToken = result.data.AccessToken;
          context.currUser = result.data.User;
          context.userName = result.data.User.UserName;
          context.userNameFull = result.data.User.Name;
          context.userEmail = result.data.User.Email;
          context.userLogin = true;
          context.tokenReady = true;

          onLoginChange(true);
        })
        .catch(err => {
          console.error(err);
          setValMsgs("Invalid username or password");
        });
    },
    [context, onLoginChange]
  );

  const validate = values => {
    const errors = {};
    if (!values.username) errors.username = "Enter user name";
    if (!values.password) errors.password = "Enter password";
    return errors;
  };

  const scrollToError = (touched, errors) => {
    const targetId =
      (touched.username && errors.username && "username") ||
      (touched.password && errors.password && "password") ||
      "username";
    document.getElementById(targetId)?.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center"
    });
  };

  if (context.userLogin) {
    onLoginChange(true);
    return <div>user is logged in</div>;
  }

  if (isFirstLogin()) {
    if (outlookComposeMode) {
      if (currEmail?.item?.conversationId) {
        setPage("emailTrackingTaskDetails");
      } else {
        setPage("emailTracking");
      }
    } else {
      setPage("onboarding");
      return <div>user's first time logging in</div>;
    }
  }

  return (
    <div className="container fluid mt-3 mt-1">
      <div className="row text-center">
        <div className="col-12 mt-3">
          <div className="mb-3">
            <img src={logo} className="loginLogo mb-3" alt="login logo" />
          </div>
          { loginSSO ?
            <>
              <div className="img-container m-1">
                <img
                  src={tokenExpiredPlaceholder}
                  className="center token-expired-placeholder"
                  alt="token expired placeholder"
                />
              </div>
              <h5 className="mt-3 mb-1 bold appFont">Your Session has Expired</h5>
              <h5 className="mt-1 mb-2 bold appFont">Please Log in</h5>
              <p className="mt-1 mb-2 bold appFontSmallOne">
                To continue managing Tasks and Projects, please re-authenticate
                your TRIYO Add‑in by clicking the button below.
              </p>
              <p className="mt-1 mb-2 bold appFontSmallOne">
                You will be directed to the TRIYO Web Application in a new
                browser window. Once successfully logged in, your Add‑in will be
                re‑authenticated automatically.
              </p>
              <p className="mt-1 mb-5 bold appFontSmallOne">
                Feel free to close the TRIYO Web Application once signed in to
                the Add‑in.
              </p>
              <button
                onClick={openWebPage}
                className="loginButton btn-primary btn-block shadow-none blueButton"
              >
                Log in via SSO
              </button>
            </>
          :
            <>
              <h4 className="mb-5 bold appFont">Log in to your account please</h4>
              { authType === "AzureAD" ?
                <button className="btn-primary" onClick={loginAzureAd}>
                  Sign In with Azure AD
                </button>
                :
                <LoginForm
                  onSubmit={onSubmit}
                  passwordType={passwordType}
                  showHidePassword={showHidePassword}
                  valMsgs={valMsgs}
                  applicationEndpoint={applicationEndpoint}
                  freemiumUser={freemiumUser}
                  scrollToError={scrollToError}
                  setValMsgs={setValMsgs}
                  validate={validate}
                />
              }
            </>
          }
        </div>
      </div>
    </div>
  );
}

Login.propTypes = {
  onLoginChange: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired
};

export default Login;
